import { ReactElement, ReactNode } from "react";
import { t } from "@lingui/core/macro";

import { PartialMachineListing } from "_/components/machine-listing";
import { HomeWhatsNew } from "_/components/home-whats-new";
import { ProjectCards } from "_/components/project-card";
import { QueueTable } from "_/components/queue/table";
import { PrintingTable } from "_/components/jobs/printing-table";
import { HistoryTable } from "_/components/jobs/history-table";
import { PermissionTest } from "_/components/permission-test";
import { permissions as rbacPermissions } from "_/data/rbac/definitions";

import { useQueueRows } from "_/components/queue/useQueueRows";
import { useJobsData, JobRow } from "_/components/jobs/useJobsData";

import * as S from "./styled";

const QUEUE_WIDGET_ROW_LIMIT = 6;

const SPACER_COUNT = 20;

const ProjectsWidget = () => (
  <S.HomeWidget>
    <S.HomeWidgetTitleContainer>
      <h2>{t`common.projects`}</h2>
    </S.HomeWidgetTitleContainer>
    <S.Carousel>
      <ProjectCards limit={10} />
      {Array.from({ length: SPACER_COUNT }, (_, i) => (
        <div key={i + 1000} />
      ))}
    </S.Carousel>
  </S.HomeWidget>
);

const MachinesWidget = () => (
  <S.HomeWidget>
    <S.HomeWidgetTitleContainer>
      <h2>{t`common.machines`}</h2>
    </S.HomeWidgetTitleContainer>
    <PartialMachineListing />
  </S.HomeWidget>
);

const PrintingWidget = ({
  printingRows,
  isLoading,
}: {
  printingRows: JobRow[];
  isLoading: boolean;
}) => (
  <S.HomeWidget>
    <S.HomeWidgetTitleContainer>
      <h2>{t`components.jobs.printing`}</h2>
    </S.HomeWidgetTitleContainer>
    <PrintingTable rows={printingRows} loading={isLoading} compact />
  </S.HomeWidget>
);

const QueueHistoryWidgets = ({
  historyRows,
  isLoading,
}: {
  historyRows: JobRow[];
  isLoading: boolean;
}) => {
  const queueProps = useQueueRows();

  return (
    <S.QueueHistoryGrid>
      <S.QueueColumn>
        <PermissionTest requiredPermissions={rbacPermissions.queue.read}>
          <S.HomeWidget>
            <S.HomeWidgetTitleContainer>
              <h2>{t`components.jobs.queue`}</h2>
            </S.HomeWidgetTitleContainer>
            <QueueTable
              {...queueProps}
              rows={queueProps.rows.slice(0, QUEUE_WIDGET_ROW_LIMIT)}
              loading={queueProps.isLoading}
              compact
              showActions
              showDrag={queueProps.canEdit}
              noDataLabel={t`components.queue.empty`}
            />
          </S.HomeWidget>
        </PermissionTest>
      </S.QueueColumn>
      <S.HistoryColumn>
        <S.HomeWidget>
          <S.HomeWidgetTitleContainer>
            <h2>{t`components.jobs.history`}</h2>
          </S.HomeWidgetTitleContainer>
          <HistoryTable rows={historyRows} loading={isLoading} compact />
        </S.HomeWidget>
      </S.HistoryColumn>
    </S.QueueHistoryGrid>
  );
};

/**
 * Placeholder for a link that doesn't do anything yet.
 */
export const NotImplementedLink = ({ children }: { children: ReactNode }) => (
  <a href="#" onClick={(e) => e.preventDefault()}>
    {children}
  </a>
);

/**
 * View for displaying the user's home page dashboard.
 *
 * @category pages
 */
export const HomeDashboard = (): ReactElement => {
  const { printingRows, historyRows, isLoading } = useJobsData();

  return (
    <S.OuterWrapper>
      <S.InnerWrapper>
        <ProjectsWidget />
        <S.MachinesPrintingGrid>
          <S.MachinesColumn>
            <MachinesWidget />
          </S.MachinesColumn>
          <S.PrintingColumn>
            <PrintingWidget printingRows={printingRows} isLoading={isLoading} />
          </S.PrintingColumn>
        </S.MachinesPrintingGrid>
        <QueueHistoryWidgets historyRows={historyRows} isLoading={isLoading} />
      </S.InnerWrapper>
      <S.RightPane>
        <HomeWhatsNew />
      </S.RightPane>
    </S.OuterWrapper>
  );
};
